import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Label, TextField } from '@printtat/ui';

interface CoverageBracket {
  max: number;
  label: string;
  price: number;
}

interface SurfaceOptionChoice {
  value: string;
  label: string;
  price: number;
}

interface SurfaceOptionDef {
  key: string;
  label: string;
  type: string;
  choices: SurfaceOptionChoice[];
  default: string;
}

interface PricingConfig {
  currency: string;
  basePrintPrice: number;
  surfacePrices: Record<string, number>;
  coverageBrackets: Record<string, CoverageBracket[]>;
  surfaceOptions?: Record<string, SurfaceOptionDef[]>;
}

interface PricingEditorProps {
  value: PricingConfig;
  surfaces: string[];
  onChange: (value: PricingConfig) => void;
}

export const PricingEditor = ({ value, surfaces, onChange }: PricingEditorProps) => {
  const [expandedBrackets, setExpandedBrackets] = useState<Set<string>>(new Set());
  const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());

  const toggleBrackets = (surface: string) => {
    setExpandedBrackets((prev) => {
      const next = new Set(prev);
      if (next.has(surface)) next.delete(surface);
      else next.add(surface);
      return next;
    });
  };

  const toggleOptions = (surface: string) => {
    setExpandedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(surface)) next.delete(surface);
      else next.add(surface);
      return next;
    });
  };

  // --- General fields ---

  const updateField = <K extends keyof PricingConfig>(field: K, val: PricingConfig[K]) => {
    onChange({ ...value, [field]: val });
  };

  // --- Surface prices ---

  const updateSurfacePrice = (surface: string, price: string) => {
    const num = parseFloat(price);
    if (isNaN(num)) return;
    onChange({
      ...value,
      surfacePrices: { ...value.surfacePrices, [surface]: num },
    });
  };

  // --- Coverage brackets ---

  const updateBracket = (surface: string, index: number, field: keyof CoverageBracket, val: string) => {
    const brackets = [...(value.coverageBrackets[surface] ?? [])];
    const num = parseFloat(val);
    if (field === 'label') {
      brackets[index] = { ...brackets[index], label: val };
    } else {
      if (isNaN(num)) return;
      brackets[index] = { ...brackets[index], [field]: num };
    }
    onChange({
      ...value,
      coverageBrackets: { ...value.coverageBrackets, [surface]: brackets },
    });
  };

  const addBracket = (surface: string) => {
    const existing = value.coverageBrackets[surface] ?? [];
    const lastMax = existing.length > 0 ? existing[existing.length - 1].max : 0;
    const newBracket: CoverageBracket = { max: Math.min(lastMax + 25, 100), label: 'New', price: 0 };
    onChange({
      ...value,
      coverageBrackets: { ...value.coverageBrackets, [surface]: [...existing, newBracket] },
    });
  };

  const removeBracket = (surface: string, index: number) => {
    const brackets = (value.coverageBrackets[surface] ?? []).filter((_, i) => i !== index);
    onChange({
      ...value,
      coverageBrackets: { ...value.coverageBrackets, [surface]: brackets },
    });
  };

  // --- Surface options ---

  const surfaceOptions = value.surfaceOptions ?? {};

  const addSurfaceOption = (surface: string) => {
    const existing = surfaceOptions[surface] ?? [];
    const newOpt: SurfaceOptionDef = {
      key: `option${existing.length + 1}`,
      label: 'New Option',
      type: 'select',
      choices: [{ value: 'default', label: 'Default', price: 0 }],
      default: 'default',
    };
    onChange({
      ...value,
      surfaceOptions: { ...surfaceOptions, [surface]: [...existing, newOpt] },
    });
  };

  const removeSurfaceOption = (surface: string, index: number) => {
    const options = (surfaceOptions[surface] ?? []).filter((_, i) => i !== index);
    const updated = { ...surfaceOptions, [surface]: options };
    if (options.length === 0) delete updated[surface];
    onChange({ ...value, surfaceOptions: updated });
  };

  const updateOption = (surface: string, index: number, field: keyof SurfaceOptionDef, val: string) => {
    const options = [...(surfaceOptions[surface] ?? [])];
    options[index] = { ...options[index], [field]: val };
    onChange({
      ...value,
      surfaceOptions: { ...surfaceOptions, [surface]: options },
    });
  };

  const addChoice = (surface: string, optIndex: number) => {
    const options = [...(surfaceOptions[surface] ?? [])];
    const opt = options[optIndex];
    options[optIndex] = {
      ...opt,
      choices: [...opt.choices, { value: '', label: '', price: 0 }],
    };
    onChange({
      ...value,
      surfaceOptions: { ...surfaceOptions, [surface]: options },
    });
  };

  const removeChoice = (surface: string, optIndex: number, choiceIndex: number) => {
    const options = [...(surfaceOptions[surface] ?? [])];
    const opt = options[optIndex];
    options[optIndex] = {
      ...opt,
      choices: opt.choices.filter((_, i) => i !== choiceIndex),
    };
    onChange({
      ...value,
      surfaceOptions: { ...surfaceOptions, [surface]: options },
    });
  };

  const updateChoice = (surface: string, optIndex: number, choiceIndex: number, field: keyof SurfaceOptionChoice, val: string) => {
    const options = [...(surfaceOptions[surface] ?? [])];
    const opt = options[optIndex];
    const choices = [...opt.choices];
    if (field === 'price') {
      const num = parseFloat(val);
      if (isNaN(num)) return;
      choices[choiceIndex] = { ...choices[choiceIndex], price: num };
    } else {
      choices[choiceIndex] = { ...choices[choiceIndex], [field]: val };
    }
    options[optIndex] = { ...opt, choices };
    onChange({
      ...value,
      surfaceOptions: { ...surfaceOptions, [surface]: options },
    });
  };

  return (
    <div className="space-y-4">
      {/* General */}
      <div className="border rounded-xl bg-white p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">General</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="block text-xs text-gray-500 mb-1">Currency</Label>
            <span className="inline-flex items-center h-9 px-3 text-sm text-gray-500 bg-gray-100 border rounded-lg">
              {value.currency}
            </span>
          </div>
          <div>
            <Label className="block text-xs text-gray-500 mb-1">Base Print Price</Label>
            <TextField
              type="number"
              min="0"
              step="0.01"
              value={String(value.basePrintPrice)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const num = parseFloat(e.target.value);
                if (!isNaN(num)) updateField('basePrintPrice', num);
              }}
            />
          </div>
        </div>
      </div>

      {/* Surface Prices */}
      <div className="border rounded-xl bg-white p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Surface Prices</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {surfaces.map((surface) => (
            <div key={surface}>
              <Label className="block text-xs text-gray-500 mb-1">{surface}</Label>
              <TextField
                type="number"
                min="0"
                step="0.01"
                value={String(value.surfacePrices[surface] ?? 0)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSurfacePrice(surface, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Coverage Brackets */}
      <div className="border rounded-xl bg-white p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Coverage Brackets</h4>
        <div className="space-y-2">
          {surfaces.map((surface) => {
            const isExpanded = expandedBrackets.has(surface);
            const brackets = value.coverageBrackets[surface] ?? [];

            return (
              <div key={surface} className="border rounded-lg">
                <button
                  onClick={() => toggleBrackets(surface)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown size={12} className="text-gray-400" /> : <ChevronRight size={12} className="text-gray-400" />}
                    <span className="text-sm text-gray-700">{surface}</span>
                    <span className="text-xs text-gray-400">{brackets.length} bracket{brackets.length !== 1 ? 's' : ''}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t pt-2 space-y-2">
                    {brackets.map((bracket, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <div>
                            <Label className="block text-[10px] text-gray-400 mb-0.5">Max %</Label>
                            <TextField
                              type="number"
                              min="0"
                              max="100"
                              value={String(bracket.max)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBracket(surface, i, 'max', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="block text-[10px] text-gray-400 mb-0.5">Label</Label>
                            <TextField
                              value={bracket.label}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBracket(surface, i, 'label', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="block text-[10px] text-gray-400 mb-0.5">Price</Label>
                            <TextField
                              type="number"
                              min="0"
                              step="0.01"
                              value={String(bracket.price)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateBracket(surface, i, 'price', e.target.value)}
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => removeBracket(surface, i)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors mt-3"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addBracket(surface)}>
                      <Plus size={12} className="mr-1" />
                      Add Bracket
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Surface Options */}
      <div className="border rounded-xl bg-white p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Surface Options</h4>
        <div className="space-y-2">
          {surfaces.map((surface) => {
            const isExpanded = expandedOptions.has(surface);
            const options = surfaceOptions[surface] ?? [];

            return (
              <div key={surface} className="border rounded-lg">
                <button
                  onClick={() => toggleOptions(surface)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown size={12} className="text-gray-400" /> : <ChevronRight size={12} className="text-gray-400" />}
                    <span className="text-sm text-gray-700">{surface}</span>
                    <span className="text-xs text-gray-400">{options.length} option{options.length !== 1 ? 's' : ''}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t pt-2 space-y-3">
                    {options.map((opt, optIdx) => (
                      <div key={optIdx} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="grid grid-cols-3 gap-2 flex-1">
                            <div>
                              <Label className="block text-[10px] text-gray-400 mb-0.5">Key</Label>
                              <TextField
                                value={opt.key}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOption(surface, optIdx, 'key', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="block text-[10px] text-gray-400 mb-0.5">Label</Label>
                              <TextField
                                value={opt.label}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOption(surface, optIdx, 'label', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="block text-[10px] text-gray-400 mb-0.5">Default</Label>
                              <TextField
                                value={opt.default}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOption(surface, optIdx, 'default', e.target.value)}
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => removeSurfaceOption(surface, optIdx)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors ml-2"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>

                        {/* Choices */}
                        <div className="space-y-1">
                          <Label className="text-[10px] text-gray-400 block">Choices</Label>
                          {opt.choices.map((choice, cIdx) => (
                            <div key={cIdx} className="flex items-center gap-2">
                              <TextField
                                placeholder="Value"
                                value={choice.value}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateChoice(surface, optIdx, cIdx, 'value', e.target.value)}
                                className="flex-1"
                              />
                              <TextField
                                placeholder="Label"
                                value={choice.label}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateChoice(surface, optIdx, cIdx, 'label', e.target.value)}
                                className="flex-1"
                              />
                              <TextField
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Price"
                                value={String(choice.price)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateChoice(surface, optIdx, cIdx, 'price', e.target.value)}
                                className="w-20"
                              />
                              <button
                                onClick={() => removeChoice(surface, optIdx, cIdx)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => addChoice(surface, optIdx)}>
                            <Plus size={10} className="mr-1" />
                            Add Choice
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addSurfaceOption(surface)}>
                      <Plus size={12} className="mr-1" />
                      Add Option
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
